import React, { useEffect, useState } from 'react'
import { Download, Printer, RefreshCw, Search, AlertTriangle } from 'lucide-react'
import * as XLSX from 'xlsx'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  fetchProductBomCostReportApi,
  type ProductBomCostReportData,
} from '@/services/reports/reportsApi'
import { formatDateFull } from '@/lib/dateFormat'

export function ProductBomCostReportPage({ initialData }: { initialData?: ProductBomCostReportData } = {}): React.ReactElement {
  const [data, setData] = useState<ProductBomCostReportData | null>(initialData ?? null)
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 250)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadData = async (query = debouncedSearch) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchProductBomCostReportApi({ q: query })
      setData(res)
    } catch (err: any) {
      setError(err?.message || 'Lỗi tải báo cáo chi phí đóng gói & ngũ kim')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData(debouncedSearch)
  }, [debouncedSearch])

  const exportExcel = () => {
    if (!data || data.items.length === 0) return
    const exportRows = data.items.map((row, idx) => ({
      'STT': idx + 1,
      'Mã hàng': row.productCode,
      'Tên sản phẩm': row.productName,
      'Khách hàng': row.customerCode || '—',
      'Tổ hợp thuộc tính': row.combinationLabel,
      'Chi phí Đóng gói (gồm VAT)': row.packagingCost,
      'Chi phí Ngũ kim (gồm VAT)': row.hardwareCost,
      'Tổng chi phí (đ)': row.totalCost,
      'Ngũ kim chưa ánh xạ': row.hardwareUnmappedCount > 0 ? `${row.hardwareUnmappedCount} dòng` : '0',
    }))

    // Add summary row at bottom
    exportRows.push({
      'STT': '' as any,
      'Mã hàng': 'TỔNG CỘNG',
      'Tên sản phẩm': `(${data.summary.totalProducts} mã hàng, ${data.summary.totalRows} tổ hợp)`,
      'Khách hàng': '',
      'Tổ hợp thuộc tính': '',
      'Chi phí Đóng gói (gồm VAT)': data.summary.grandPackagingTotal,
      'Chi phí Ngũ kim (gồm VAT)': data.summary.grandHardwareTotal,
      'Tổng chi phí (đ)': data.summary.grandCombinedTotal,
      'Ngũ kim chưa ánh xạ': data.summary.grandHardwareUnmappedCount > 0 ? `${data.summary.grandHardwareUnmappedCount} dòng` : '0',
    })

    const ws = XLSX.utils.json_to_sheet(exportRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'ChiPhiDongGoiNguKim')
    XLSX.writeFile(wb, `ChiPhiDongGoi_NguKim_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const money = (val: number | null | undefined) =>
    val != null && Number.isFinite(val) && val > 0 ? Math.round(val).toLocaleString('vi-VN') + ' đ' : '0 đ'

  return (
    <div className="space-y-4 print:space-y-2">
      <style>{`
        @page { size: 210mm 297mm; margin: 8mm; }
        @media print {
          body { font-family: 'Inter', sans-serif !important; background: #fff !important; color: #000 !important; }
          thead { display: table-header-group !important; }
          tr { break-inside: avoid !important; page-break-inside: avoid !important; }
          th, td, div, span, p { overflow: visible !important; text-overflow: clip !important; }
          .print-border-solid { border: 1px solid #000 !important; border-radius: 0 !important; }
          .print-border-solid th,
          .print-border-solid td { border: 1px solid #000 !important; border-radius: 0 !important; }
          .no-print { display: none !important; }
          * { border-radius: 0 !important; }
        }
      `}</style>

      {/* Screen Header */}
      <div className="no-print space-y-2">
        <Breadcrumb items={[{ label: 'Trang chủ', to: '/' }, { label: 'Báo cáo', to: '/reports' }, { label: 'Báo cáo chi phí Đóng gói & Ngũ kim' }]} />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <PageHeader
            title="Báo Cáo Chi Phí Đóng Gói & Ngũ Kim Theo List Hàng"
            subtitle="Tổng hợp chi phí định mức đóng gói và ngũ kim theo từng sản phẩm và tổ hợp thuộc tính"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => loadData(debouncedSearch)} disabled={loading}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
            <Button variant="secondary" onClick={exportExcel} disabled={!data || data.items.length === 0}>
              <Download className="mr-1.5 h-4 w-4 text-emerald-700" />
              Xuất Excel
            </Button>
            <Button variant="primary" onClick={() => window.print()} disabled={!data || data.items.length === 0}>
              <Printer className="mr-1.5 h-4 w-4" />
              In / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {data && (
        <div className="no-print grid grid-cols-2 gap-3 sm:grid-cols-5">
          <KpiCard label="Số tổ hợp BOM" value={String(data.summary.totalRows)} hint="Dòng báo cáo" />
          <KpiCard label="Số mã hàng" value={String(data.summary.totalProducts)} hint="Sản phẩm" />
          <KpiCard label="Tổng tiền đóng gói" value={money(data.summary.grandPackagingTotal)} hint="Gồm VAT" />
          <KpiCard label="Tổng tiền ngũ kim" value={money(data.summary.grandHardwareTotal)} hint="Gồm VAT" />
          <KpiCard label="Tổng cộng chi phí" value={money(data.summary.grandCombinedTotal)} hint="Đóng gói + Ngũ kim" />
        </div>
      )}

      {/* Search Bar */}
      <div className="no-print flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 shadow-xs">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Lọc / tìm kiếm theo mã sản phẩm hoặc tên sản phẩm..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full text-xs outline-none"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="text-xs text-slate-400 hover:text-slate-600 font-semibold px-1"
          >
            Xóa
          </button>
        )}
      </div>

      {/* Print Title Header */}
      <div className="hidden print:block pb-2 border-b border-black text-center mb-3">
        <h1 className="text-base font-bold uppercase tracking-wider text-black">
          BÁO CÁO TỔNG CHI PHÍ ĐÓNG GÓI & NGŨ KIM THEO LIST HÀNG
        </h1>
        <p className="text-[11px] text-black">
          MỘC PHÁT FURNITURE ERP — NGÀY XUẤT: {formatDateFull(new Date())}
        </p>
      </div>

      {loading && <PageLoading />}
      {error && <PageError message={error} />}

      {!loading && !error && data && data.items.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-xs text-slate-500">
          Không tìm thấy mã hàng nào phù hợp điều kiện tìm kiếm.
        </div>
      )}

      {/* Main Table */}
      {!loading && !error && data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-none border border-slate-300 bg-white shadow-none print:border-black print:rounded-none print:shadow-none">
          <table className="w-full text-left text-xs border-collapse print-border-solid">
            <thead>
              <tr className="bg-slate-100 text-slate-800 font-bold text-[11px] print:bg-slate-200 print:text-black border-b border-slate-300 print:border-black">
                <th className="px-2 py-2 text-center w-10">STT</th>
                <th className="px-3 py-2 w-28 font-mono">Mã hàng</th>
                <th className="px-3 py-2 min-w-[160px]">Tên sản phẩm</th>
                <th className="px-2 py-2 text-center w-20">Khách hàng</th>
                <th className="px-3 py-2 min-w-[180px]">Tổ hợp thuộc tính</th>
                <th className="px-3 py-2 text-right w-32">Chi phí Đóng gói</th>
                <th className="px-3 py-2 text-right w-32">Chi phí Ngũ kim</th>
                <th className="px-3 py-2 text-right w-36">Tổng chi phí</th>
                <th className="px-2 py-2 text-center w-24 no-print">Trạng thái</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 print:divide-black">
              {data.items.map((row, idx) => (
                <tr key={`${row.productId}-${row.combinationLabel}-${idx}`} className="hover:bg-slate-50 print:hover:bg-transparent">
                  <td className="px-2 py-1.5 text-center font-mono text-[11px] text-slate-500 print:text-black">
                    {idx + 1}
                  </td>
                  <td className="px-3 py-1.5 font-mono font-bold text-blue-700 print:text-black">
                    {row.productCode}
                  </td>
                  <td className="px-3 py-1.5 font-semibold text-slate-900 print:text-black">
                    {row.productName}
                  </td>
                  <td className="px-2 py-1.5 text-center text-slate-700 print:text-black">
                    {row.customerCode || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-slate-800 print:text-black">
                    <span className="font-medium">{row.combinationLabel}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap text-slate-900 print:text-black">
                    {row.packagingCost > 0 ? money(row.packagingCost) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap text-slate-900 print:text-black">
                    {row.hardwareCost > 0 ? money(row.hardwareCost) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap font-black text-blue-900 print:text-black">
                    {row.totalCost > 0 ? money(row.totalCost) : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center no-print">
                    {row.hardwareUnmappedCount > 0 ? (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-300"
                        title={`${row.hardwareUnmappedCount} dòng ngũ kim chưa ánh xạ vật tư trong danh mục`}
                      >
                        <AlertTriangle className="h-3 w-3 text-amber-600" />
                        {row.hardwareUnmappedCount} chưa map
                      </span>
                    ) : (
                      <span className="text-[10px] text-emerald-700 font-medium">Đủ</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 print:bg-slate-200 font-bold border-t-2 border-slate-400 print:border-black text-xs">
                <td colSpan={5} className="px-3 py-2 text-right uppercase tracking-wider text-slate-900 print:text-black">
                  Tổng cộng toàn bảng ({data.summary.totalProducts} mã hàng / {data.summary.totalRows} tổ hợp):
                </td>
                <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-black text-slate-900 print:text-black">
                  {money(data.summary.grandPackagingTotal)}
                </td>
                <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-black text-slate-900 print:text-black">
                  {money(data.summary.grandHardwareTotal)}
                </td>
                <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-black text-emerald-700 print:text-black text-sm">
                  {money(data.summary.grandCombinedTotal)}
                </td>
                <td className="px-2 py-2 text-center text-[10px] text-slate-500 font-normal no-print">
                  {data.summary.grandHardwareUnmappedCount > 0 && `${data.summary.grandHardwareUnmappedCount} chưa map`}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
